import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBuilding } from "react-icons/fa";
import { useSelector } from "react-redux";
import moment from "moment";
import styles from "../../../master/academicConfiguration/Academiclist.module.css";
import payStyles from "../../../../parent/feesManagement/PayFees.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
  PayOnlineModal,
} from "../../../../../components";
import PopupModal from "../../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../../services/Api";
import PayOfflineModal from "../../../../../components/PayOfflineModal/PayOfflineModal";

// payment_mode: 0 = cash, 1 = online
const PAYMENT_MODE_VALUES = { cash: 0, online: 1 };
// fee_status: 1 = paid
const FEE_STATUS_PAID = 1;

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `${num < 0 ? "-" : ""}₹${Math.abs(num).toLocaleString("en-IN")}`;
};

/* Per-line discount for the UNPAID path (GroupView) — mirrors
   PayFees.jsx's getDiscountValue. GroupView doesn't currently send
   `type`, so this resolves to FLAT until the API adds it.        */
const getDiscountValue = (item) => {
  const amount = Number(item.amount) || 0;
  const discountAmount = Number(item.discount_amount) || 0;
  return Number(item.type) === 1
    ? (amount * discountAmount) / 100
    : discountAmount;
};

const ViewCollectFees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";
  const academic_year = clientData?.academic_year || "";
  // Group fees for admission collection are set up under the upcoming
  // academic year, so the GroupList lookup keys off is_upcoming_year.
  const upcoming_academic_year_id = clientData?.is_upcoming_year || "";

  // ── Values handed over by the list's View button ──
  const admissionId = location.state?.id ?? "";
  const admissionType = location.state?.admission_type ?? 0;
  const applicationNo = location.state?.application_no ?? "";

  const [loading, setLoading] = useState(false);
  const [feeData, setFeeData] = useState(null); // FeeView -> student details
  const [classList, setClassList] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupDetail, setGroupDetail] = useState(null); // GroupView -> fee items
  const [groupDetailLoading, setGroupDetailLoading] = useState(false);

  // stage 2 opens only after Proceed succeeds (unpaid flow only)
  const [expanded, setExpanded] = useState(false);

  const [formData, setFormData] = useState({ paymentMode: "online" });
  const [cashAmount, setCashAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [cashModalOpen, setCashModalOpen] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  /* ── FeeView: student details (+ payment_history when paid) ──
       POST schoolFees/admission/FeeView
       { id, school_id, academic_year_id, admission_type }        */
  const fetchFeeView = useCallback(async () => {
    if (!admissionId || !school_id) return;
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/admission/FeeView",
        {
          id: Number(admissionId),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
          admission_type: Number(admissionType),
        },
      );
      if (response?.data?.status) {
        setFeeData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching fee view:", error);
    } finally {
      setLoading(false);
    }
  }, [admissionId, school_id, academic_year_id, admissionType]);

  useEffect(() => {
    fetchFeeView();
  }, [fetchFeeView]);

  /* ── READ-ONLY MODE ──
       FeeView sends fee_status as a STRING here ("Paid"/"Not Paid"),
       unlike the list which sends 1/0. A paid student has a recorded
       payment_history: show it and disable everything.            */
  const isPaid = useMemo(() => {
    const s = String(feeData?.fee_status ?? "")
      .trim()
      .toLowerCase();
    return s === "paid" || Number(feeData?.fee_status) === FEE_STATUS_PAID;
  }, [feeData]);

  // the recorded payment (most recent first entry)
  const paidRecord = useMemo(
    () => feeData?.payment_history?.[0] ?? null,
    [feeData],
  );

  const readOnly = isPaid && Boolean(paidRecord);

  /* ── school/classList ──
       Turns FeeView's `assigned_class` ("VI") into class_id (66),
       which GroupList filters on and feePayment requires.
       NOTE: `domain_id` holds the class NAME, not an id.
       Skipped entirely in read-only mode — nothing needs it.      */
  const fetchClassList = useCallback(async () => {
    if (!school_id || !academic_year || readOnly) return;
    try {
      const response = await AxiosInstance.post("/school/classList", {
        id: Number(school_id),
        academic_year: academic_year,
      });
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setClassList(
          response.data.data.map((item) => ({
            class_id: item.class_id,
            class_name: item.domain_id, // domain_id = the NAME
          })),
        );
      } else {
        setClassList([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setClassList([]);
    }
  }, [school_id, academic_year, readOnly]);

  useEffect(() => {
    fetchClassList();
  }, [fetchClassList]);

  const resolvedClassId = useMemo(() => {
    const className = feeData?.assigned_class;
    if (!className) return location.state?.class_id ?? "";
    const match = classList.find(
      (c) =>
        String(c.class_name).trim().toLowerCase() ===
        String(className).trim().toLowerCase(),
    );
    return match?.class_id ?? location.state?.class_id ?? "";
  }, [feeData, classList, location.state]);

  const studentDetails = useMemo(
    () => ({
      admission_id: feeData?.application_no || applicationNo,
      student_name: feeData?.student_name || "",
      class_name: feeData?.assigned_class || "",
      section: feeData?.assigned_section || location.state?.section || "",
      emis_id: feeData?.emis_id || location.state?.emis_id || "",
      fee_status: feeData?.fee_status ?? "",
      class_id: resolvedClassId,
    }),
    [feeData, applicationNo, resolvedClassId, location.state],
  );

  /* ── GroupList: the Group Fee dropdown (unpaid only) ── */
  const fetchGroupList = useCallback(async () => {
    if (!school_id || !upcoming_academic_year_id || readOnly) return;
    setGroupLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/feeGroup/GroupList",
        {
          school_id: Number(school_id),
          academic_year_id: Number(upcoming_academic_year_id),
          search: "",
          class_id: studentDetails.class_id || "",
          section_id: studentDetails.section || "",
          page: "",
          limit: "",
        },
      );
      if (response?.data?.status && Array.isArray(response.data.data)) {
        setGroupOptions(
          response.data.data.map((item) => ({
            value: String(item.id),
            label: item.group_name,
          })),
        );
      } else {
        setGroupOptions([]);
      }
    } catch (error) {
      console.error("Error fetching group list:", error);
      setGroupOptions([]);
    } finally {
      setGroupLoading(false);
    }
  }, [
    school_id,
    upcoming_academic_year_id,
    studentDetails.class_id,
    studentDetails.section,
    readOnly,
  ]);

  useEffect(() => {
    fetchGroupList();
  }, [fetchGroupList]);

  /* ── Proceed -> GroupView (unpaid only) ── */
  const handleProceed = async () => {
    if (!selectedGroup) {
      setErrors({
        groupFee: labels.groupFeeRequired || "Group Fee is required",
      });
      return;
    }
    setGroupDetailLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/feeGroup/GroupView",
        {
          id: Number(selectedGroup),
          school_id: Number(school_id),
          academic_year_id: Number(upcoming_academic_year_id),
        },
      );
      if (response?.data?.status && response?.data?.data) {
        setGroupDetail(response.data.data);
        setExpanded(true);
      } else {
        setGroupDetail(null);
        setExpanded(false);
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Could not load this fee group.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error fetching group view:", error);
      setGroupDetail(null);
      setExpanded(false);
      setModalState({
        open: true,
        type: "error",
        message: "Could not load this fee group. Please try again.",
        redirectPath: "",
      });
    } finally {
      setGroupDetailLoading(false);
    }
  };

  const handleGroupChange = (value) => {
    const val = typeof value === "object" ? value?.value : value;
    setSelectedGroup(val ?? "");
    setExpanded(false);
    setGroupDetail(null);
    setErrors((prev) => ({ ...prev, groupFee: "" }));
  };

  /* ── Fee rows ──
       PAID  -> payment_history[0].fee_details (what was charged)
       UNPAID-> groupDetail.fee_items (what will be charged)      */
  const feesRows = useMemo(() => {
    const items = readOnly
      ? paidRecord?.fee_details || []
      : groupDetail?.fee_items || [];

    const rows = items.map((item, i) => ({
      id: `fee-${item.id ?? item.fee_type_id ?? i}`,
      fee_type: item.fee_type_name,
      amount: Number(item.amount) || 0,
      isDiscount: false,
    }));
    items
      .filter((item) => item.discount_id && Number(item.discount_amount))
      .forEach((item, i) => {
        rows.push({
          id: `disc-${item.id ?? item.fee_type_id ?? i}`,
          fee_type: item.discount_name,
          amount: -(Number(item.discount_amount) || 0),
          isDiscount: true,
        });
      });
    return rows;
  }, [readOnly, paidRecord, groupDetail]);

  /* ── Payment summary ──
       PAID  -> use the RECORDED totals verbatim. They are what was
                actually charged and paid, so they are authoritative
                even though they don't equal sum(fee_details).
                See notes: the API's total_amount (45000) does not
                match sum(fee_details) (50900).
       UNPAID-> compute from the group's fee items.               */
  const paymentSummary = useMemo(() => {
    if (readOnly && paidRecord) {
      const total = Number(paidRecord.total_amount) || 0;
      const discount = Number(paidRecord.total_discount) || 0;
      const net = Number(paidRecord.net_amount) || 0;
      const paid = Number(paidRecord.paid_amount) || 0;
      const balance = Number(paidRecord.balance_amount) || 0;
      return {
        dueDate: paidRecord.date || "-",
        totalFees: total,
        discountApplied: discount,
        fine: 0,
        totalPaid: paid,
        netPayable: net,
        totalAmountToPay: balance,
      };
    }

    const items = groupDetail?.fee_items || [];
    const totalFees = items.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    );
    const discountApplied = items.reduce(
      (sum, item) => sum + (item.discount_id ? getDiscountValue(item) : 0),
      0,
    );
    const isPastDue = groupDetail?.due_date
      ? moment().isAfter(moment(groupDetail.due_date))
      : false;
    const fine = isPastDue ? Number(groupDetail?.fine_amount) || 0 : 0;
    const totalPaid = Number(groupDetail?.total_paid) || 0;
    const netPayable = totalFees - discountApplied;
    return {
      dueDate: groupDetail?.due_date
        ? moment(groupDetail.due_date).format("DD/MM/YYYY")
        : "-",
      totalFees,
      discountApplied,
      fine,
      totalPaid,
      netPayable,
      totalAmountToPay: Math.max(netPayable + fine - totalPaid, 0),
    };
  }, [readOnly, paidRecord, groupDetail]);

  /* Seed the cash amount to the full balance when cash is picked. */
  useEffect(() => {
    if (!readOnly && formData.paymentMode === "cash") {
      setCashAmount(String(paymentSummary.totalAmountToPay || ""));
      setAmountError("");
    }
  }, [readOnly, formData.paymentMode, paymentSummary.totalAmountToPay]);

  const validateCashAmount = (value) => {
    const raw = String(value).trim();
    if (!raw) return "Enter a valid amount";
    const num = Number(raw);
    if (Number.isNaN(num) || num <= 0) return "Enter a valid amount";
    if (num > paymentSummary.totalAmountToPay) {
      return `Amount cannot exceed ${formatCurrency(
        paymentSummary.totalAmountToPay,
      )}`;
    }
    return "";
  };

  const handleCashAmountChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d.]/g, "");
    setCashAmount(cleaned);
    setAmountError(validateCashAmount(cleaned));
  };

  const hasDuesPending = paymentSummary.totalAmountToPay > 0;

  const handleProceedToPay = () => {
    if (!hasDuesPending || readOnly) return;
    if (formData.paymentMode === "cash") {
      const err = validateCashAmount(cashAmount);
      if (err) {
        setAmountError(err);
        return;
      }
      setCashModalOpen(true);
    } else {
      setPayModalOpen(true);
    }
  };

  /* ── Submit: schoolFees/admission/feePayment ── */
  const submitPayment = async ({
    paidAmount,
    paymentMode,
    upiTransactionId = null,
    orderId = null,
    transId = null,
  }) => {
    setSubmitting(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/admission/feePayment",
        {
          id: Number(admissionId),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
          admission_type: Number(admissionType),
          application_no: studentDetails.admission_id,
          // GroupView returns class_id directly — more reliable
          // than resolving the class name.
          class_id:
            Number(groupDetail?.class_id) ||
            Number(studentDetails.class_id) ||
            null,
          section: studentDetails.section || "",
          emis_id: studentDetails.emis_id || "",
          fee_group_id: Number(selectedGroup),
          fee_status: FEE_STATUS_PAID,
          upi_transaction_id: upiTransactionId,
          total_amount: paymentSummary.totalFees,
          total_discount: paymentSummary.discountApplied,
          net_amount: paymentSummary.netPayable,
          paid_amount: paidAmount,
          payment_mode: paymentMode,
          order_id: orderId,
          trans_id: transId,
          created_by: user?.id,
        },
      );

      if (response?.data?.status) {
        setCashModalOpen(false);
        setPayModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            `Payment of ${formatCurrency(paidAmount)} recorded successfully!`,
          redirectPath: -1,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Payment could not be recorded.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          "Payment could not be recorded. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCashCollect = (paidFromModal) => {
    const paidAmount = Number(paidFromModal ?? cashAmount) || 0;
    if (paidAmount <= 0 || paidAmount > paymentSummary.totalAmountToPay) return;
    submitPayment({ paidAmount, paymentMode: PAYMENT_MODE_VALUES.cash });
  };

  const handlePaySubmit = ({ upiTransactionId, amountPayingNow }) => {
    const paidAmount = Math.min(
      Number(amountPayingNow) || 0,
      paymentSummary.totalAmountToPay,
    );
    if (paidAmount <= 0) return;
    submitPayment({
      paidAmount,
      paymentMode: PAYMENT_MODE_VALUES.online,
      upiTransactionId,
    });
  };

  const handleCancel = () => navigate(-1);

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success") navigate(redirectPath || -1);
  };

  const SectionTitle = ({ children }) => (
    <div className={styles.header}>
      <div className={styles.headerIcon}>
        <FaBuilding />
      </div>
      <h2 className={styles.headerTitle}>{children}</h2>
    </div>
  );

  // widgets show immediately when read-only; otherwise only after Proceed
  const showWidgets = readOnly || (expanded && groupDetail);

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? labels.success || "Success"
            : labels.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* pay modals never mount in read-only mode */}
      {!readOnly && (
        <>
          <PayOnlineModal
            open={payModalOpen}
            onClose={() => setPayModalOpen(false)}
            onSubmit={handlePaySubmit}
            submitting={submitting}
            schoolId={school_id}
            totalAmount={paymentSummary.totalAmountToPay}
            paymentMode="Online"
            student={{
              name: studentDetails.student_name,
              admissionId: studentDetails.admission_id,
              classSection: [studentDetails.class_name, studentDetails.section]
                .filter(Boolean)
                .join("-"),
            }}
          />
          <PayOfflineModal
            open={cashModalOpen}
            onClose={() => setCashModalOpen(false)}
            onConfirm={handleCashCollect}
            submitting={submitting}
            amount={Number(cashAmount) || 0}
            totalDue={paymentSummary.totalAmountToPay}
            paymentMode="Cash"
            student={{
              name: studentDetails.student_name,
              admissionId: studentDetails.admission_id,
              classSection: [studentDetails.class_name, studentDetails.section]
                .filter(Boolean)
                .join("-"),
            }}
          />
        </>
      )}

      <div className={styles.container}>
        {/* ═══ Student Details ═══ */}
        <SectionTitle>
          {labels.studentDetails || "Student Details"}
        </SectionTitle>
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={labels.admissionId || "Admission ID"}
              name="admission_id"
              value={studentDetails.admission_id}
              onChange={() => {}}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={labels.studentName || "Student Name"}
              name="student_name"
              value={studentDetails.student_name}
              onChange={() => {}}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={labels.class || "Class"}
              name="class_name"
              value={studentDetails.class_name}
              onChange={() => {}}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={labels.section || "Section"}
              name="section"
              value={studentDetails.section}
              onChange={() => {}}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* ═══ Fees Details ═══
                    PAID   -> the recorded group as a read-only field
                    UNPAID -> the selectable Group Fee dropdown        */}
        <div className={styles.sectionSpacing}>
          <SectionTitle>{labels.feesDetails || "Fees Details"}</SectionTitle>
          <div className="row">
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              {readOnly ? (
                <CustomTextInput
                  label={labels.groupFee || "Group Fee"}
                  name="groupFee"
                  value={paidRecord?.group_name || "-"}
                  onChange={() => {}}
                  disabled
                  readOnly
                />
              ) : (
                <CustomDropdown
                  label={labels.groupFee || "Group Fee"}
                  name="groupFee"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                  options={groupOptions}
                  placeholder={
                    groupLoading
                      ? labels.loading || "Loading..."
                      : labels.selectGroupFee || "Select Group Fee"
                  }
                  required
                  error={errors.groupFee}
                  isDisabled={groupLoading}
                />
              )}
            </div>
          </div>
        </div>

        {/* ═══ Stage 1 buttons — unpaid, pre-Proceed only ═══ */}
        {!readOnly && !expanded && (
          <div className={payStyles.actionRow}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {labels.cancel || "Cancel"}
            </CustomButton>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              loading={groupDetailLoading}
              loadingText={labels.loading || "Loading..."}
              onClick={handleProceed}
            >
              {labels.proceed || "Proceed"}
            </CustomButton>
          </div>
        )}

        {/* ═══ Widgets ═══ */}
        {showWidgets && (
          <>
            <div className={payStyles.feesTableWrap}>
              <table className={payStyles.feesTable}>
                <thead>
                  <tr>
                    <th>{labels.feeType || "Fee Type"}</th>
                    <th className={payStyles.amountCol}>
                      {labels.amountRs || "Amount (₹)"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {feesRows.length === 0 && (
                    <tr>
                      <td colSpan={2} className={payStyles.emptyRow}>
                        {labels.noFeesFound || "No fees found."}
                      </td>
                    </tr>
                  )}
                  {feesRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.fee_type}</td>
                      <td className={payStyles.amountCol}>
                        {row.isDiscount
                          ? `- ${formatCurrency(Math.abs(row.amount))}`
                          : formatCurrency(row.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payment summary */}
            <div className={styles.sectionSpacing}>
              <SectionTitle>
                {labels.paymentSummary || "Payment summary"}
              </SectionTitle>
              <div className={payStyles.summaryPanel}>
                <div className={payStyles.summaryRow}>
                  <span className={payStyles.summaryLabel}>
                    {readOnly
                      ? labels.paymentDate || "Payment Date"
                      : labels.dueDate || "Due Date"}
                  </span>
                  <span className={payStyles.summaryValueBlue}>
                    {paymentSummary.dueDate}
                  </span>
                </div>
                <div className={payStyles.summaryRow}>
                  <span className={payStyles.summaryLabel}>
                    {labels.totalFees || "Total Fees"}
                  </span>
                  <span className={payStyles.summaryValueBold}>
                    {formatCurrency(paymentSummary.totalFees)}
                  </span>
                </div>
                <div className={payStyles.summaryRow}>
                  <span className={payStyles.summaryLabel}>
                    {labels.discountApplied || "Discount Applied"}
                  </span>
                  <span className={payStyles.summaryValueNegative}>
                    - {formatCurrency(paymentSummary.discountApplied)}
                  </span>
                </div>
                <div
                  className={`${payStyles.summaryRow} ${payStyles.summaryDivider}`}
                >
                  <span className={payStyles.summaryLabelBold}>
                    {labels.netPayable || "Net Payable"}
                  </span>
                  <span className={payStyles.summaryValueBold}>
                    {formatCurrency(paymentSummary.netPayable)}
                  </span>
                </div>
                {!readOnly && (
                  <div className={payStyles.summaryRow}>
                    <span className={payStyles.summaryLabel}>
                      {labels.fineAmount || "Fine"}
                    </span>
                    <span className={payStyles.summaryValue}>
                      {formatCurrency(paymentSummary.fine)}
                    </span>
                  </div>
                )}
                <div className={payStyles.summaryRow}>
                  <span className={payStyles.summaryLabel}>
                    {labels.totalPaid || "Total Paid"}
                  </span>
                  <span className={payStyles.summaryValueNegative}>
                    - {formatCurrency(paymentSummary.totalPaid)}
                  </span>
                </div>
                <div
                  className={`${payStyles.summaryRow} ${payStyles.totalPayableRow}`}
                >
                  <span className={payStyles.totalPayableLabel}>
                    {readOnly
                      ? labels.balanceAmount || "Balance"
                      : labels.totalAmountToPay || "Total Amount to be Paid"}
                  </span>
                  <span className={payStyles.totalPayableValue}>
                    {formatCurrency(paymentSummary.totalAmountToPay)}
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ Payment Mode ═══ */}
            <div className={payStyles.paymentModeSection}>
              <SectionTitle>
                {labels.paymentMode || "Payment Mode"}
              </SectionTitle>

              {readOnly ? (
                /* recorded payment — read-only facts */
                <div className={payStyles.summaryPanel}>
                  <div className={payStyles.summaryRow}>
                    <span className={payStyles.summaryLabel}>
                      {labels.paymentMode || "Payment Mode"}
                    </span>
                    <span className={payStyles.summaryValueBold}>
                      {paidRecord?.payment_mode || "-"}
                    </span>
                  </div>
                  <div className={payStyles.summaryRow}>
                    <span className={payStyles.summaryLabel}>
                      {labels.upiTransactionId || "UPI Transaction ID"}
                    </span>
                    <span className={payStyles.summaryValueBold}>
                      {paidRecord?.upi_transaction_id || "-"}
                    </span>
                  </div>
                  {/* <div className={payStyles.summaryRow}>
                    <span className={payStyles.summaryLabel}>
                      {labels.transactionId || "Transaction ID"}
                    </span>
                    <span className={payStyles.summaryValueBold}>
                      {paidRecord?.trans_id || "-"}
                    </span>
                  </div> */}
                </div>
              ) : (
                <>
                  <div className={payStyles.paymentModeOptions}>
                    <label className={payStyles.radioLabel}>
                      <input
                        type="radio"
                        name="paymentMode"
                        value="cash"
                        className={payStyles.radioInput}
                        checked={formData.paymentMode === "cash"}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            paymentMode: "cash",
                          }))
                        }
                      />
                      {labels.cash || "Cash"}
                    </label>

                    <label className={payStyles.radioLabel}>
                      <input
                        type="radio"
                        name="paymentMode"
                        value="online"
                        className={payStyles.radioInput}
                        checked={formData.paymentMode === "online"}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            paymentMode: "online",
                          }))
                        }
                      />
                      {labels.online || "Online"}
                    </label>
                  </div>

                  {formData.paymentMode === "cash" && (
                    <div className={payStyles.amountField}>
                      <label className={payStyles.amountLabel}>
                        {labels.amountPayingNow || "Amount paying now"}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={payStyles.amountInput}
                        value={cashAmount}
                        onChange={handleCashAmountChange}
                        placeholder={formatCurrency(
                          paymentSummary.totalAmountToPay,
                        )}
                      />
                      {amountError && (
                        <span className={payStyles.amountErrorText}>
                          {amountError}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* buttons: Back only when read-only */}
              <div className={payStyles.actionRow}>
                <CustomButton
                  type="button"
                  variant="contained"
                  className={styles.cancelButton}
                  fullWidth={false}
                  onClick={handleCancel}
                >
                  {readOnly ? labels.back || "Back" : labels.cancel || "Cancel"}
                </CustomButton>
                {!readOnly && (
                  <CustomButton
                    type="button"
                    variant="contained"
                    className={styles.addButton}
                    fullWidth={false}
                    disabled={!hasDuesPending}
                    onClick={handleProceedToPay}
                  >
                    {labels.proceedToPay || "Proceed to Pay"}
                  </CustomButton>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ViewCollectFees;
